import ApiHandler from "src/api/ApiHandler"
import APIRequester from "src/api/APIRequester"
import Validator from "src/validation/Validator"
import DeploymentEnvironmentConfigOperator from "src/config/DeploymentEnvironmentConfigOperator"
import DEPLOYMENT_ENVIRONMENT_CONFIG from "src/config/DeploymentEnvironmentConfig"
import EnvironmentVariableProvider from "src/config/EnvironmentVariableProvider"
import LoginHelper from "src/components/login/LoginHelper"
import PathRouter from "src/navigation/PathRouter"
import ErrorHandler from "src/components/errors/ErrorHandler"
import Loader from "src/components/general/Loading"
import Clock from "src/general/Clock"
import EventAdaptingStateHandler from "src/states/EventAdaptingStateHandler"
import BasicStateHandler from "src/states/BasicStateHandler"
import StateConstants from "src/states/StateContstants"
import LocalDatastore from "src/datastore/LocalDatastore"
import FeatureProvider from "src/features/FeatureProvider"
import featureFlagDict from "../../src/features/copied_feature_flags.json"
import ParamParser from "src/navigation/ParamParser"
import singleton from "src/config/Singleton"
import { AnalysisBuilderChain } from "src/components/areas/areaAnalysisDescription/analysis_builders/AnalysisBuilderChain"
import { SalesCountAnalysisBuilder } from "src/components/areas/areaAnalysisDescription/analysis_builders/SalesCountAnalysisBuilder"
import GrowthAnalysisBuilder from "src/components/areas/areaAnalysisDescription/analysis_builders/GrowthAnalysisBuilder"
import { MedianPriceAnalysisBuilder } from "src/components/areas/areaAnalysisDescription/analysis_builders/MedianPriceAnalysisBuilder"
import { PriceSpreadAnalysisBuilder } from "src/components/areas/areaAnalysisDescription/analysis_builders/PriceSpreadAnalysisBuilder"



class Config {
    deploymentEnvironmentConfig: DeploymentEnvironmentConfigOperator
    environmentVariableProvider: EnvironmentVariableProvider

    constructor() {
        this.environmentVariableProvider = new EnvironmentVariableProvider()
        this.deploymentEnvironmentConfig = new DeploymentEnvironmentConfigOperator(DEPLOYMENT_ENVIRONMENT_CONFIG, this.environmentVariableProvider)
    }

    @singleton()
    getPathRouter() {
        return new PathRouter()
    }

    @singleton()
    getApiHandler() {
        return new ApiHandler(
            this.getAPIRequester(),
            this.deploymentEnvironmentConfig.get("BACKEND_BASE_API_URL"),
            this.getLoginHelper(),
            this.getEventAdaptingStateHandler(),
            this.getFeatureProvider()
        )
    }

    @singleton()
    getEventAdaptingStateHandler() {
        return new EventAdaptingStateHandler(
            this.getBasicStateHandler()
        )
    }

    @singleton()
    getParamParser() {
        return new ParamParser(this.getEventAdaptingStateHandler())
    }

    @singleton()
    getBasicStateHandler() {
        return new BasicStateHandler(this.getInitialStates())
    }

    getInitialStates() {
        let initialStates: any = {}
        initialStates[StateConstants.RELATIVE_CONDITION] = 0.5
        initialStates[StateConstants.ADVANCED_OPTIONS] = false
        initialStates[StateConstants.ADDRESS_STATE] = ""
        initialStates[StateConstants.IS_LOGGED_IN] = false
        initialStates[StateConstants.SHOW_ACCOUNT_PANEL] = false
        initialStates[StateConstants.REMOVED_HOMES] = []
        initialStates[StateConstants.IS_GOOGLE_BOT] = false
        initialStates[StateConstants.IS_MOBILE] = window.innerWidth <= 992
        return initialStates
    }

    @singleton()
    getFeatureProvider() {
        return new FeatureProvider(this.getFeatureFlagDatastore())
    }

    @singleton()
    getFeatureFlagDatastore() {
        return new LocalDatastore(featureFlagDict)
    }

    @singleton()
    getClock() {
        return new Clock()
    }

    @singleton()
    getAPIRequester() {
        return new APIRequester(
            this.getValidator()
        )
    }

    @singleton()
    getValidator() {
        return new Validator()
    }

    getLoginDetails(): string {
        return this.deploymentEnvironmentConfig.get("LOGIN_DETAILS")
    }

    getLoginHelper(): LoginHelper {
        return new LoginHelper(
            this.getLoginDetails(),
            this.getClock(),
            this.getEventAdaptingStateHandler(),
            this.deploymentEnvironmentConfig.get("BACKEND_BASE_API_URL")
        )
    }

    @singleton()
    getErrorHandler(): ErrorHandler {
        return new ErrorHandler()
    }

    getLoader(): Loader {
        return new Loader()
    }

    @singleton()
    getAreaAnalysisBuilderChain(): AnalysisBuilderChain {
        return new AnalysisBuilderChain([
            new SalesCountAnalysisBuilder(),
            new GrowthAnalysisBuilder(),
            new MedianPriceAnalysisBuilder(),
            new PriceSpreadAnalysisBuilder(),
        ])
    }
}

export default Config
